import { ChevronDown, ChevronUp, Loader } from "lucide-react";

const MatrixView = ({
  course,
  academicYear,
  articulationMatrix,
  isLoading,
  darkMode,
}) => {
  if (isLoading) {
    return (
      <div
        className="p-6 rounded-lg mb-4 border flex items-center justify-center"
        style={{
          backgroundColor: darkMode ? "#161B22" : "#F8FAFC",
          borderColor: darkMode ? "#22232B" : "#E5E7EB",
          boxShadow: darkMode ? "none" : "0 1px 3px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Loader
          size={20}
          className="animate-spin mr-3"
          style={{ color: darkMode ? "#3B82F6" : "#2563EB" }}
        />
        <span style={{ color: darkMode ? "#D1D5DB" : "#4B5563" }}>
          Loading matrix data...
        </span>
      </div>
    );
  }

  if (!articulationMatrix) {
    return (
      <div
        className="p-6 rounded-lg border"
        style={{
          backgroundColor: darkMode ? "#161B22" : "#F8FAFC",
          borderColor: darkMode ? "#22232B" : "#E5E7EB",
          boxShadow: darkMode ? "none" : "0 1px 3px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div className="flex items-center mb-4">
          <div
            className="w-1 h-6 rounded-full mr-3"
            style={{ backgroundColor: darkMode ? "#8B5CF6" : "#7C3AED" }}
          ></div>
          <h4
            className="text-lg font-semibold"
            style={{ color: darkMode ? "#FFFFFF" : "#111827" }}
          >
            CO-PO-PSO Articulation Matrix for {course.course_code}
          </h4>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <svg
              className="w-12 h-12 mx-auto mb-3"
              style={{ color: darkMode ? "#4B5563" : "#9CA3AF" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
              ></path>
            </svg>
            <p style={{ color: darkMode ? "#9CA3AF" : "#6B7280" }}>
              No articulation matrix defined for academic year {academicYear}.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Extract counts from the matrix summary
  const totalCos = articulationMatrix.summary?.total_cos || 0;
  const totalPos = articulationMatrix.summary?.total_pos || 0;
  const totalPsos = articulationMatrix.summary?.total_psos || 0;

  // Helper function to get strength value for a CO-PO mapping using sequential indices
  const getStrengthValue = (coIndex, outcomeIndex, isPso = false) => {
    const matrixData = isPso
      ? articulationMatrix.co_pso_matrix
      : articulationMatrix.co_po_matrix;

    // Find the CO mapping by index
    const coMapping = matrixData[coIndex];
    if (!coMapping) return "-";

    // Find the specific outcome mapping by index
    const mapping =
      coMapping[isPso ? "pso_mappings" : "po_mappings"][outcomeIndex];
    return mapping ? mapping.strength : "-";
  };

  // Generate sequential numbers for COs, POs, and PSOs
  const coNumbers = Array.from({ length: totalCos }, (_, i) => i + 1);
  const poNumbers = Array.from({ length: totalPos }, (_, i) => i + 1);
  const psoNumbers = Array.from({ length: totalPsos }, (_, i) => i + 1);

  return (
    <div
      className="p-6 rounded-lg border"
      style={{
        backgroundColor: darkMode ? "#161B22" : "#F8FAFC",
        borderColor: darkMode ? "#22232B" : "#E5E7EB",
        boxShadow: darkMode ? "none" : "0 1px 3px rgba(0, 0, 0, 0.05)",
      }}
    >
      {/* Header Section */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div
            className="w-1 h-6 rounded-full mr-3"
            style={{ backgroundColor: darkMode ? "#8B5CF6" : "#7C3AED" }}
          ></div>
          <h4
            className="text-lg font-semibold"
            style={{ color: darkMode ? "#FFFFFF" : "#111827" }}
          >
            CO-PO-PSO Articulation Matrix for {course.course_code}
          </h4>
        </div>
        <span
          className="text-xs font-medium px-3 py-1 rounded-full"
          style={{
            color: darkMode ? "#C4B5FD" : "#5B21B6",
            backgroundColor: darkMode ? "rgba(139, 92, 246, 0.2)" : "#EDE9FE",
          }}
        >
          {academicYear}
        </span>
      </div>

      {/* Combined PO and PSO Mapping Table */}
      <div>
        <h5
          className="font-medium mb-4 text-sm"
          style={{ color: darkMode ? "#D1D5DB" : "#374151" }}
        >
          CO-PO-PSO Mapping Matrix
        </h5>

        <div
          className="overflow-x-auto rounded-lg border"
          style={{
            borderColor: darkMode ? "#22232B" : "#E5E7EB",
            boxShadow: darkMode ? "none" : "0 1px 2px rgba(0, 0, 0, 0.05)",
          }}
        >
          <table className="min-w-full">
            <thead>
              <tr style={{ backgroundColor: darkMode ? "#0D1117" : "#FFFFFF" }}>
                <th
                  className="p-3 text-left text-sm font-semibold"
                  style={{
                    color: darkMode ? "#C9CCD1" : "#374151",
                    borderBottom: darkMode
                      ? "1px solid #22232B"
                      : "1px solid #E5E7EB",
                    backgroundColor: darkMode ? "#161B22" : "#F9FAFB",
                  }}
                >
                  CO / Outcome
                </th>

                {/* PO Headers */}
                {poNumbers.map((poNumber) => (
                  <th
                    key={`po-${poNumber}`}
                    className="p-3 text-center text-sm font-semibold"
                    style={{
                      color: darkMode ? "#C9CCD1" : "#1E40AF",
                      borderBottom: darkMode
                        ? "1px solid #22232B"
                        : "1px solid #E5E7EB",
                      backgroundColor: darkMode
                        ? "rgba(59, 130, 246, 0.15)"
                        : "#EFF6FF",
                    }}
                  >
                    PO{poNumber}
                  </th>
                ))}

                {/* PSO Headers (if they exist) */}
                {psoNumbers.length > 0 &&
                  psoNumbers.map((psoNumber) => (
                    <th
                      key={`pso-${psoNumber}`}
                      className="p-3 text-center text-sm font-semibold"
                      style={{
                        color: darkMode ? "#C9CCD1" : "#5B21B6",
                        borderBottom: darkMode
                          ? "1px solid #22232B"
                          : "1px solid #E5E7EB",
                        backgroundColor: darkMode
                          ? "rgba(139, 92, 246, 0.15)"
                          : "#EDE9FE",
                      }}
                    >
                      PSO{psoNumber}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {coNumbers.map((coNumber, coIndex) => {
                return (
                  <tr
                    key={`co-${coNumber}`}
                    style={{
                      backgroundColor:
                        coIndex % 2 === 0
                          ? darkMode
                            ? "rgba(26, 31, 46, 0.3)"
                            : "#FFFFFF"
                          : darkMode
                            ? "rgba(26, 31, 46, 0.5)"
                            : "#F9FAFB",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = darkMode
                        ? "rgba(37, 99, 235, 0.1)"
                        : "#EFF6FF";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        coIndex % 2 === 0
                          ? darkMode
                            ? "rgba(26, 31, 46, 0.3)"
                            : "#FFFFFF"
                          : darkMode
                            ? "rgba(26, 31, 46, 0.5)"
                            : "#F9FAFB";
                    }}
                  >
                    <td
                      className="p-3 font-medium text-sm"
                      style={{
                        color: darkMode ? "#FFFFFF" : "#111827",
                        borderBottom: darkMode
                          ? "1px solid #22232B"
                          : "1px solid #F3F4F6",
                        backgroundColor: darkMode ? "#161B22" : "#F3F4F6",
                      }}
                    >
                      CO{coNumber}
                    </td>

                    {/* PO Values */}
                    {poNumbers.map((poNumber, poIndex) => {
                      const strength = getStrengthValue(coIndex, poIndex);
                      return (
                        <td
                          key={`co-${coNumber}-po-${poNumber}`}
                          className="p-3 text-center text-sm font-bold"
                          style={{
                            borderBottom: darkMode
                              ? "1px solid #22232B"
                              : "1px solid #F3F4F6",
                            color:
                              strength === "-"
                                ? darkMode
                                  ? "#9CA3AF"
                                  : "#6B7280"
                                : strength >= 2
                                  ? "#16A34A"
                                  : "#DC2626",
                            backgroundColor:
                              strength !== "-"
                                ? strength >= 2
                                  ? darkMode
                                    ? "rgba(34, 197, 94, 0.1)"
                                    : "rgba(34, 197, 94, 0.05)"
                                  : darkMode
                                    ? "rgba(239, 68, 68, 0.1)"
                                    : "rgba(239, 68, 68, 0.05)"
                                : "transparent",
                          }}
                        >
                          {strength}
                        </td>
                      );
                    })}

                    {/* PSO Values (if they exist) */}
                    {psoNumbers.map((psoNumber, psoIndex) => {
                      const strength = getStrengthValue(
                        coIndex,
                        psoIndex,
                        true
                      );
                      return (
                        <td
                          key={`co-${coNumber}-pso-${psoNumber}`}
                          className="p-3 text-center text-sm font-bold"
                          style={{
                            borderBottom: darkMode
                              ? "1px solid #22232B"
                              : "1px solid #F3F4F6",
                            color:
                              strength === "-"
                                ? darkMode
                                  ? "#9CA3AF"
                                  : "#6B7280"
                                : strength >= 2
                                  ? "#16A34A"
                                  : "#DC2626",
                            backgroundColor:
                              strength !== "-"
                                ? strength >= 2
                                  ? darkMode
                                    ? "rgba(34, 197, 94, 0.1)"
                                    : "rgba(34, 197, 94, 0.05)"
                                  : darkMode
                                    ? "rgba(239, 68, 68, 0.1)"
                                    : "rgba(239, 68, 68, 0.05)"
                                : "transparent",
                          }}
                        >
                          {strength}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Legend and Summary */}
        <div
          className="mt-6 p-4 rounded-lg border"
          style={{
            backgroundColor: darkMode ? "#0D1117" : "#FFFFFF",
            borderColor: darkMode ? "#22232B" : "#E5E7EB",
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Legend */}
            <div>
              <h6
                className="font-medium mb-2 text-sm"
                style={{ color: darkMode ? "#D1D5DB" : "#374151" }}
              >
                Legend
              </h6>
              <div className="flex flex-wrap gap-3 text-xs">
                <div className="flex items-center">
                  <div
                    className="w-3 h-3 mr-2 rounded-sm"
                    style={{
                      backgroundColor: darkMode
                        ? "rgba(59, 130, 246, 0.3)"
                        : "#EFF6FF",
                    }}
                  ></div>
                  <span style={{ color: darkMode ? "#C9CCD1" : "#4B5563" }}>
                    Program Outcomes (PO)
                  </span>
                </div>
                {psoNumbers.length > 0 && (
                  <div className="flex items-center">
                    <div
                      className="w-3 h-3 mr-2 rounded-sm"
                      style={{
                        backgroundColor: darkMode
                          ? "rgba(139, 92, 246, 0.3)"
                          : "#EDE9FE",
                      }}
                    ></div>
                    <span style={{ color: darkMode ? "#C9CCD1" : "#4B5563" }}>
                      Program Specific Outcomes (PSO)
                    </span>
                  </div>
                )}
                <div className="flex items-center">
                  <div className="w-3 h-3 mr-2 rounded-sm bg-green-600"></div>
                  <span style={{ color: darkMode ? "#C9CCD1" : "#4B5563" }}>
                    Strong Mapping (≥2)
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 mr-2 rounded-sm bg-red-600"></div>
                  <span style={{ color: darkMode ? "#C9CCD1" : "#4B5563" }}>
                    Weak Mapping (&lt;2)
                  </span>
                </div>
              </div>
            </div>

            {/* Summary Information */}
            <div>
              <h6
                className="font-medium mb-2 text-sm"
                style={{ color: darkMode ? "#D1D5DB" : "#374151" }}
              >
                Summary
              </h6>
              <div
                className="text-xs"
                style={{ color: darkMode ? "#9CA3AF" : "#6B7280" }}
              >
                <p style={{ color: darkMode ? "#9CA3AF" : "#4B5563" }}>
                  {totalCos} Course Outcomes, {totalPos} Program Outcomes, and{" "}
                  {totalPsos} Program Specific Outcomes
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatrixView;
